package org.moxultimate.topwithmox.components.image

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.chip.Chip
import org.moxultimate.topwithmox.navigation.rememberAppNavigator
import org.moxultimate.topwithmox.theme.BlackBG
import org.moxultimate.topwithmox.theme.DarkTextColor
import org.moxultimate.topwithmox.theme.FontFamily
import org.moxultimate.topwithmox.theme.WhiteTextColor
import org.moxultimate.topwithmox.wallpaper.data.WallpaperResponse

/**
 * wallpaperAspectRatio
 * Maps a wallpaper type to its width / height ratio: phone and vertical 9:16,
 * widescreen 21:9, desktop (and unknown types) 16:9.
 *
 * @param type Wallpaper type string as stored in the database, case-insensitive.
 * @return Width divided by height.
 */
fun wallpaperAspectRatio(type: String): Double = when (type.lowercase()) {
    "phone", "vertical" -> 9.0 / 16.0
    "widescreen"        -> 21.0 / 9.0
    else                -> 16.0 / 9.0
}

/**
 * WallpaperDisplay
 * Renders one wallpaper card with hover overlay, click handling and a name/type caption.
 *
 * @param wallpaper Wallpaper to render.
 * @param onWallpaperClick Optional click override; defaults to opening the details page.
 * @param hoverText Text shown over the image on hover.
 * @param fillColumn When true (big screens), the image fills its parent column width and
 * reserves height from the type's aspect ratio, so a masonry parent can size the column.
 */
@Composable
fun WallpaperDisplay(
    wallpaper: WallpaperResponse,
    onWallpaperClick: ((WallpaperResponse) -> Unit)? = null,
    hoverText: String = "Click to Download",
    fillColumn: Boolean = false,
) {
    val navigator = rememberAppNavigator()
    var isHovered by remember { mutableStateOf(false) }
    val breakpoint = rememberBreakpoint()
    val isMobile = breakpoint <= Breakpoint.SM

    /**
     * On mobile: phone cards are half-width (2 per row), all other types fill full width.
     * On desktop: cards are fixed-clamp sizes driven by viewport width.
     * columnModifier controls the flex-item width; imageModifier controls image dimensions.
     */
    val columnModifier = remember(wallpaper.type, isMobile) {
        if (isMobile) {
            when (wallpaper.type.lowercase()) {
                "phone" -> Modifier.styleModifier { property("width", "calc(50% - 8px)") }
                else    -> Modifier.fillMaxWidth()
            }
        } else {
            Modifier
        }
    }

    val wallpaperModifier = remember(wallpaper.type, isMobile, fillColumn) {
        if (isMobile) {
            when (wallpaper.type.lowercase()) {
                // Width-only for all types — height auto-scales from the image's natural ratio
                "phone"      -> Modifier.fillMaxWidth()
                "desktop"    -> Modifier.fillMaxWidth()
                "widescreen" -> Modifier.fillMaxWidth()
                "vertical"   -> Modifier.fillMaxWidth()
                else         -> Modifier.fillMaxWidth()
            }
        } else if (fillColumn) {
            Modifier.fillMaxWidth().styleModifier {
                property("aspect-ratio", wallpaperAspectRatio(wallpaper.type).toString())
                property("object-fit", "cover")
            }
        } else {
            Modifier.width(clamp(min = 300.px, value = 25.vw, max = 480.px))
        }
    }

    Column(
        columnModifier.padding(bottom = 16.px)
    ) {

        Box(
            modifier = PressableWallpaperStyle
                .toModifier()
                .position(Position.Relative)
                .overflow(Overflow.Hidden)
                .onClick { onWallpaperClick?.invoke(wallpaper) ?: navigator.goToWallpaperDetailsPage(wallpaper) }
                .onMouseEnter { isHovered = true }
                .onMouseLeave { isHovered = false },
            contentAlignment = Alignment.Center
        ) {

            // Your Wallpaper Image
            Image(
                src = wallpaper.thumbnailPath.orEmpty(),
                description = "${wallpaper.name} — ${wallpaper.type} wallpaper from the ${wallpaper.series} series",
                modifier = wallpaperModifier
                    .borderRadius(12.px)
                    .attrsModifier { attr("loading", "lazy") }
                    .onContextMenu { e ->
                        e.preventDefault()
                    }.onDragStart { e ->
                        e.preventDefault()
                    }
            )

            if (!wallpaper.panoramaPath.isNullOrBlank()) {
                Box(
                    modifier = Modifier
                        .position(Position.Absolute)
                        .top(8.px)
                        .right(8.px)
                        .styleModifier { property("pointer-events", "none") }
                ) {
                    Chip(description = "360°", gradientText = true, compact = isMobile)
                }
            }

            // Dark Dim Overlay (Visible only on hover)
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .position(Position.Absolute)
                    .backgroundColor(BlackBG)
                    .borderRadius(12.px)
                    .styleModifier {
                        // Smoothly animate the opacity shift
                        property("opacity", if (isHovered) "0.2" else "0")
                        property("transition", "opacity 0.2s ease")
                        property("pointer-events", "none")
                    }
            )

            // "Click to Download" Text (Visible and scaled only on hover)
            SpanText(
                text = hoverText,
                modifier = Modifier
                    .position(Position.Absolute)
                    .color(WhiteTextColor)
                    .fontWeight(FontWeight.SemiBold)
                    .fontSize(16.px)
                    .styleModifier {
                        property("pointer-events", "none")
                        property("opacity", if (isHovered) "1" else "0")
                        property("transform", if (isHovered) "scale(1)" else "scale(0.9)")
                        property("transition", "all 0.2s ease")
                    }
            )
        }

        WallpaperDetailsText(wallpaper)
    }
}

@Composable
private fun WallpaperDetailsText(
    wallpaper: WallpaperResponse,
    modifier: Modifier = Modifier,
) {
    Column(modifier.padding(top = 6.px)) {

        SpanText(
            text = wallpaper.name,
            modifier = modifier
                .color(DarkTextColor)
                .fontFamily(FontFamily)
                .letterSpacing(0.8.px)
                .fontSize(clamp(min = 12.px, value = 0.8.vw, max = 14.px))
                .fontWeight(FontWeight.SemiBold)
        )

        SpanText(
            text = wallpaper.type + " · " + wallpaper.series + " Series",
            modifier = modifier
                .color(DarkTextColor)
                .fontFamily(FontFamily)
                .letterSpacing(0.8.px)
                .fontSize(clamp(min = 10.px, value = 0.8.vw, max = 12.px))
                .fontWeight(FontWeight.Thin)
        )
    }
}

val PressableWallpaperStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("cursor", "pointer")
            property("transition", "transform 0.15s ease, opacity 0.15s ease")
        }
    }
    hover {
        Modifier.styleModifier {
            property("transform", "scale(1.02)")
        }
    }
    active {
        Modifier.styleModifier {
            property("transform", "scale(0.90)")
            property("opacity", "0.80")
        }
    }
}